import React, { FC, useRef } from 'react';
import { withTheme } from 'styled-components';

import bindHandlers from '../../utils/handlerHelper';
import Handler from '../../commonTypes/handler.types';
import useControlledState from '../hooks/useControlledState/useControlledState';
import { Basepaths } from './selectableBasepaths.style';
import { basePathDataValueKey, homeBase, firstBase, secondBase, thirdBase } from '../Basepaths/constants';
import { SelectableBasepathsProps, SelectableBasepathsSelectedProps, BasepathsProps } from './selectableBasepaths.types';

import {
  generateColors,
  buildStateObject,
} from './utils';

const SelectableBasepaths: FC<SelectableBasepathsProps> = ({
    width,
    altText,
    handlers = {},
    showHome = false,
    enableHomeEvents = false,
    selected,
    defaultSelected,
    strokeWidth,
    theme = {},
    className,
  }: SelectableBasepathsProps) => {

    const [selectedState, setSelectedState] = useControlledState({
        controlled: buildStateObject(selected),
        default: buildStateObject(defaultSelected),
        name: 'SelectableBasepaths',
    });

    const focusedTarget = useRef<HTMLElement | null>(null);

    const home = selectedState ? selectedState[homeBase] : false;
    const first = selectedState ? selectedState[firstBase] : false;
    const second = selectedState ? selectedState[secondBase] : false;
    const third = selectedState ? selectedState[thirdBase] : false;

    const {
      onClick,
      onChange,
      ...otherHandlers
  } = handlers;

    const internalClickHandler = (event: React.MouseEvent | React.KeyboardEvent) => {

      if(onClick) {
          onClick(event);
      }

      const target: HTMLElement | null = event.target as HTMLElement;
      const selectedValue: string | null | undefined = target?.getAttribute(basePathDataValueKey);

      if(!selectedValue) {
          return;
      }

      const isSelected: boolean = selectedState ? selectedState[selectedValue] : false;
      const newSelectedMap: object = {
          ...selectedState,
      };

      if(isSelected) {
          delete newSelectedMap[selectedValue];
      } else {
          newSelectedMap[selectedValue] = true;
      }

      setSelectedState(newSelectedMap);

      if (onChange) {

        const newSelected: Array<string> = Object.keys(newSelectedMap);
        const changes: SelectableBasepathsSelectedProps = {
            label: selectedValue,
            value: selectedValue,
        };
        onChange({
            selected: newSelected.map((value) => {
                const selectedProps: SelectableBasepathsSelectedProps = {
                    label: value,
                    value,
                };
                return selectedProps;
            }),
            changes,
            event: isSelected ? 'deselect' : 'select',
        });

      }

    };

    const internalHandlers: Handler = {
      onClick: internalClickHandler,
      onKeyDown: (event: React.KeyboardEvent) => {
        const { key }: any = event;

        if(key === 'Enter' || key ===  " ") {
            if(focusedTarget.current) {
                focusedTarget.current.focus();
            }
            internalClickHandler(event);
        } else if(key === 'Tab') {
            if(focusedTarget.current) {
                focusedTarget.current.focus();
                focusedTarget.current = null;
            }
        }
      },
    }

    const combinedHandlers: Handler = bindHandlers(
      null,
      otherHandlers,
      internalHandlers,
    );

    const basepathsColors: BasepathsProps = generateColors(typeof theme?.dark !== 'undefined');

    return (
        <Basepaths
            className={ className }
            home={ home }
            first={ first }
            second={ second }
            third={ third }
            altText={ altText }
            width={ width }
            strokeWidth={ strokeWidth }
            showHome={ showHome }
            enableHomeEvents={ enableHomeEvents }
            handlers={ combinedHandlers }
            { ...basepathsColors }
        />
    );
  };

  export default withTheme(SelectableBasepaths);
